import { useEffect, useState, useCallback } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Shield, Bot, KeyRound, Copy, Check } from "lucide-react";
import ModerationModal from "@/components/ModerationModal";
import LiveRoom from "@/components/LiveRoom";
import BroadcastModal from "@/components/BroadcastModal";
import ReportsTab from "@/pages/admin/ReportsTab";
import UsersTab from "@/pages/admin/UsersTab";
import LiveDiagnosticsTab from "@/pages/admin/LiveDiagnosticsTab";
import PostsTab from "@/pages/admin/PostsTab";
import CommunitiesTab from "@/pages/admin/CommunitiesTab";
import GlobalCommunitiesTab from "@/pages/admin/GlobalCommunitiesTab";
import LiveTab from "@/pages/admin/LiveTab";
import InboxesTab from "@/pages/admin/InboxesTab";
import RecapTab from "@/pages/admin/RecapTab";
import AmbassadorsTab from "@/pages/admin/AmbassadorsTab";
import FlyersTab from "@/pages/admin/FlyersTab";
import AuditTab from "@/pages/admin/AuditTab";
import DareLibraryTab from "@/pages/admin/DareLibraryTab";
import AgeVerificationPanel from "@/pages/admin/AgeVerificationPanel";
import ConversionsTab from "@/pages/admin/ConversionsTab";
import ReferralsTab from "@/pages/admin/ReferralsTab";
import NeoInboxTab from "@/pages/admin/NeoInboxTab";
import ChallengesTab from "@/pages/admin/ChallengesTab";
import DashboardTab from "@/pages/admin/DashboardTab";
import AdminTierBadge from "@/pages/admin/AdminTierBadge";
import { AdminSidebar, AdminMobileStrip } from "@/pages/admin/AdminNav";
import { visibleSections } from "@/pages/admin/adminSections";

export default function AdminPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const sections = visibleSections(user);
  const requested = params.get("tab") || "dashboard";
  const tab = sections.some((x) => x.v === requested) ? requested : "dashboard";
  const setTab = (v) => {
    const next = params.get("embed") === "1" ? { embed: "1" } : {};
    if (v !== "dashboard") next.tab = v;
    setParams(next, { replace: false });
  };
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [posts, setPosts] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [live, setLive] = useState([]);
  const [reports, setReports] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [convoMessages, setConvoMessages] = useState({});
  const [openConvo, setOpenConvo] = useState(null);
  const [modTarget, setModTarget] = useState(null);
  const [ghostStream, setGhostStream] = useState(null);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [pwResult, setPwResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const loadStats = useCallback(() => api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}), []);
  const loadAnalytics = useCallback(() => api.get("/admin/analytics/summary").then(({ data }) => setAnalytics(data)).catch(() => {}), []);
  const loadUsers = useCallback((query = "") => api.get("/admin/users", { params: { q: query || undefined } }).then(({ data }) => setUsersList(data)).catch(() => {}), []);
  const loadPosts = useCallback(() => api.get("/admin/posts").then(({ data }) => setPosts(data)).catch(() => {}), []);
  const loadCommunities = useCallback(() => api.get("/communities").then(({ data }) => setCommunities(data)).catch(() => {}), []);
  const loadLive = useCallback(() => api.get("/admin/live").then(({ data }) => setLive(data)).catch(() => {}), []);
  const loadReports = useCallback(() => api.get("/admin/reports").then(({ data }) => setReports(data)).catch(() => {}), []);
  const loadConversations = useCallback(() => api.get("/admin/conversations").then(({ data }) => setConversations(data)).catch(() => {}), []);

  useEffect(() => {
    if (user?.role !== "admin") return;
    loadStats();
    loadAnalytics();
    loadUsers("");
    loadPosts();
    loadCommunities();
    loadLive();
    loadReports();
    loadConversations();
  }, [user, loadStats, loadAnalytics, loadUsers, loadPosts, loadCommunities, loadLive, loadReports, loadConversations]);

  const openConversation = async (id) => {
    setOpenConvo(id);
    try {
      const { data } = await api.get(`/conversations/${id}/messages`);
      setConvoMessages((m) => ({ ...m, [id]: data }));
    } catch (e) {
      toast.error("Could not load thread.");
    }
  };

  if (user === null) return null;
  if (!user || user.role !== "admin") return <Navigate to="/feed" replace />;

  // --- User actions ---
  const ban = async (id) => { await api.post(`/admin/users/${id}/ban`); toast.success("User banned."); loadUsers(""); loadStats(); };
  const unban = async (id) => { await api.post(`/admin/users/${id}/unban`); toast.success("User unbanned."); loadUsers(""); loadStats(); };
  const suspend = async (u) => {
    const raw = window.prompt(
      `Suspend ${u.display_name} for how many hours?\n(1-720 — e.g. 24 = 1 day, 168 = 1 week)`,
      "24",
    );
    if (raw === null) return;
    const hours = parseInt(raw, 10);
    if (!hours || hours < 1 || hours > 720) { toast.error("Enter 1-720 hours."); return; }
    try {
      await api.post(`/admin/users/${u.id}/suspend`, { hours });
      toast.success(`${u.display_name} suspended for ${hours}h.`);
      loadUsers(""); loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Suspend failed."); }
  };
  const toggleCreator = async (u) => {
    await api.post(`/admin/users/${u.id}/creator`, { is_creator: !u.is_creator });
    toast.success(!u.is_creator ? "Marked as creator." : "Removed creator status.");
    loadUsers(""); loadStats();
  };
  const grantLifetimeBoss = async (u) => {
    try {
      const { data } = await api.post("/admin/owner/lifetime-boss", { target_handle: u.handle, confirm: "GRANT" });
      toast.success(data.already_granted ? "Lifetime Boss badge was already granted — no change." : "Permanent Boss badge granted.");
      loadUsers("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Grant refused."); }
  };
  const toggleAdmin = async (u) => {
    const next = u.role === "admin" ? "user" : "admin";
    try {
      await api.post(`/admin/users/${u.id}/role`, { role: next });
      toast.success(`Role set to ${next}.`);
      loadUsers(""); loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Role change failed."); }
  };
  const toggleTechAdmin = async (u) => {
    const next = u.role === "admin" && u.admin_scope === "technical" ? "user" : "tech_admin";
    try {
      await api.post(`/admin/users/${u.id}/role`, { role: next });
      toast.success(next === "tech_admin" ? "Technical Admin role assigned." : "Technical Admin role removed.");
      loadUsers(""); loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Role change failed."); }
  };
  const deleteUser = async (id) => { await api.delete(`/admin/users/${id}`); toast.success("User deleted."); loadUsers(""); loadStats(); };
  const grantCoins = async (id) => { await api.post(`/admin/users/${id}/coins`, { delta: 500 }); toast.success("+500 coins"); loadUsers(""); };
  const togglePaying = async (u) => {
    const next = !u.is_paying_customer;
    await api.post(`/admin/users/${u.id}/paying-customer`, { is_paying: next });
    toast.success(next ? "Marked as paying customer" : "Removed paying status");
    loadUsers("");
  };
  const resetPassword = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/set-password`, {});
      setPwResult({ kind: "set", ...data });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reset password"); }
  };
  const sendPasswordResetEmail = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/send-password-reset`);
      setPwResult({ kind: "email", ...data });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send reset"); }
  };
  const setUserHandle = async (u) => {
    // Small prompt-based UX — admin usage is low-volume; a full modal would
    // be overkill here. Empty string clears the handle entirely (rare).
    const next = window.prompt(
      `Rename ${u.display_name}'s username.\n\n` +
      `New handle (letters/numbers/._- only, 2-30 chars).\n` +
      `Leave blank + OK to clear the handle.`,
      u.handle || "",
    );
    if (next === null) return; // cancel
    const cleaned = (next || "").trim();
    if (cleaned && cleaned === u.handle) return; // no-op
    try {
      const { data } = await api.post(`/admin/users/${u.id}/handle`, {
        handle: cleaned || null,
      });
      toast.success(data.handle ? `Renamed to ${data.display_name}` : "Handle cleared.");
      loadUsers("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed.");
    }
  };
  const copyPwValue = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { toast.error("Copy failed"); }
  };

  // --- Post / community / live actions ---
  const deletePost = async (id) => { await api.delete(`/admin/posts/${id}`); toast.success("Post deleted."); loadPosts(); loadStats(); };
  const deleteCommunity = async (id) => { await api.delete(`/admin/communities/${id}`); toast.success("Community deleted."); loadCommunities(); loadStats(); };
  const forceEndLive = async (id) => { await api.post(`/admin/live/${id}/force-end`); toast.success("Stream ended."); loadLive(); loadStats(); };
  const startRecording = async (stream) => {
    try { await api.post(`/admin/live/${stream.id}/record/start`); toast.success("Recording started."); loadLive(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Recording failed to start."); }
  };
  const stopRecording = async (stream) => {
    try { await api.post(`/admin/live/${stream.id}/record/stop`); toast.success("Recording stopped."); loadLive(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Stop failed."); }
  };
  const resolveReport = async (id, action) => {
    await api.post(`/admin/reports/${id}/resolve`, { action });
    toast.success(
      action === "dismissed" ? "Report dismissed."
      : action === "content_removed" ? "Content removed."
      : "User banned."
    );
    loadReports(); loadStats(); loadUsers(""); loadPosts(); loadLive();
  };

  return (
    <div data-testid="admin-root" className="p-6 lg:p-8">
      <header className="lg:sticky top-[calc(52px+env(safe-area-inset-top))] lg:top-0 -mx-6 lg:-mx-8 px-6 lg:px-8 py-4 glass border-b border-white/5 mb-6 z-10 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <Shield size={22} className="text-rose-500 mt-1" />
          <div>
            <h1 className="font-display text-2xl tracking-tight" data-testid="admin-title">Admin Control Center</h1>
            <p className="text-xs text-zinc-400 mt-0.5" data-testid="admin-subtitle">Manage users, live content, communities, reports, and platform operations.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={() => setBroadcastOpen(true)}
            className="btn-crimson rounded-none flex items-center gap-2"
            data-testid="admin-broadcast-button"
          >
            <Bot size={16} /> <span className="hidden sm:inline">Broadcast to all members</span><span className="sm:hidden">Broadcast</span>
          </Button>
          <AdminTierBadge user={user} />
        </div>
      </header>

      <AdminMobileStrip sections={sections} active={tab} onSelect={setTab} badges={{ reports: stats?.reports?.open }} />

      <div className="lg:flex lg:gap-8 items-start">
        <AdminSidebar sections={sections} active={tab} onSelect={setTab} badges={{ reports: stats?.reports?.open }} />

      <Tabs value={tab} onValueChange={setTab} className="w-full min-w-0">
        <TabsContent value="dashboard" className="mt-0">
          <DashboardTab stats={stats} analytics={analytics} sections={sections} onOpen={setTab} user={user} />
        </TabsContent>

        <TabsContent value="reports" className="mt-0">
          <ReportsTab reports={reports} onResolve={resolveReport} onModerate={setModTarget} />
        </TabsContent>

        <TabsContent value="live-diagnostics" className="mt-0">
          <LiveDiagnosticsTab />
        </TabsContent>
        <TabsContent value="users" className="mt-0">
          <UsersTab
            users={usersList}
            me={user}
            onSearch={loadUsers}
            onToggleCreator={toggleCreator}
            onToggleAdmin={toggleAdmin}
            onToggleTechAdmin={toggleTechAdmin}
            onGrantLifetimeBoss={grantLifetimeBoss}
            onGrantCoins={grantCoins}
            onTogglePaying={togglePaying}
            onResetPassword={resetPassword}
            onSendResetEmail={sendPasswordResetEmail}
            onSetHandle={setUserHandle}
            onBan={ban}
            onUnban={unban}
            onSuspend={suspend}
            onDelete={deleteUser}
          />
        </TabsContent>

        <TabsContent value="posts" className="mt-0">
          <PostsTab posts={posts} onDelete={deletePost} />
        </TabsContent>

        <TabsContent value="communities" className="mt-0">
          <CommunitiesTab communities={communities} onDelete={deleteCommunity} />
        </TabsContent>

        <TabsContent value="global-communities" className="mt-0">
          <GlobalCommunitiesTab />
        </TabsContent>

        <TabsContent value="live" className="mt-0">
          <LiveTab
            hideGhost={user?.admin_scope === "technical"}
            streams={live}
            onGhost={setGhostStream}
            onStartRec={startRecording}
            onStopRec={stopRecording}
            onForceEnd={forceEndLive}
          />
        </TabsContent>

        <TabsContent value="inboxes" className="mt-0">
          <InboxesTab
            conversations={conversations}
            openConvo={openConvo}
            convoMessages={convoMessages}
            onOpen={openConversation}
          />
        </TabsContent>

        <TabsContent value="recap" className="mt-0">
          <RecapTab adminUserId={user?.id} />
        </TabsContent>

        <TabsContent value="ambassadors" className="mt-0">
          <AmbassadorsTab />
        </TabsContent>

        <TabsContent value="flyers" className="mt-0">
          <FlyersTab />
        </TabsContent>

        <TabsContent value="audit" className="mt-0">
          <AuditTab />
        </TabsContent>
        <TabsContent value="guardian" className="mt-0">
          <AgeVerificationPanel />
        </TabsContent>

        <TabsContent value="conversions" className="mt-0">
          <ConversionsTab />
        </TabsContent>

        <TabsContent value="referrals" className="mt-0">
          <ReferralsTab />
        </TabsContent>

        <TabsContent value="neo" className="mt-0">
          <NeoInboxTab me={user} />
        </TabsContent>

        <TabsContent value="challenges" className="mt-0">
          <ChallengesTab me={user} />
        </TabsContent>

        <TabsContent value="dares" className="mt-0">
          <DareLibraryTab />
        </TabsContent>
      </Tabs>
      </div>

      {modTarget && (
        <ModerationModal
          open={!!modTarget}
          onOpenChange={(v) => !v && setModTarget(null)}
          target={modTarget}
        />
      )}
      {ghostStream && (
        <LiveRoom
          stream={{ ...ghostStream, ghost: true }}
          onClose={() => setGhostStream(null)}
        />
      )}
      <BroadcastModal open={broadcastOpen} onOpenChange={setBroadcastOpen} />

      <Dialog open={!!pwResult} onOpenChange={(o) => !o && setPwResult(null)}>
        <DialogContent className="glass rounded-none max-w-md border-amber-400/40" data-testid="pw-result-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <KeyRound size={18} className="text-amber-400" />
              {pwResult?.kind === "set" ? "Password reset" : "Reset link generated"}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs uppercase tracking-widest">
              {pwResult?.display_name} · {pwResult?.email}
            </DialogDescription>
          </DialogHeader>

          {pwResult?.kind === "set" && (
            <>
              <p className="text-sm text-zinc-400 leading-relaxed">
                New temporary password. <span className="text-amber-400">Copy it now — you won't be able to see it again.</span> Text or DM it to the user; they can log in and change it under Profile → Edit.
              </p>
              <div className="flex items-center gap-2">
                <div className="flex-1 font-mono text-lg bg-black/60 border border-amber-400/50 px-4 py-3 text-white select-all">
                  {pwResult.password}
                </div>
                <Button onClick={() => copyPwValue(pwResult.password)} className="btn-crimson rounded-none px-4 py-6" data-testid="pw-copy-button">
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                </Button>
              </div>
            </>
          )}

          {pwResult?.kind === "email" && (
            <>
              <div className={`text-sm border-l-2 pl-3 py-2 ${pwResult.email_sent ? "border-emerald-500 text-emerald-400" : "border-amber-400 text-amber-400"}`}>
                {pwResult.email_sent
                  ? `Email dispatched to ${pwResult.email}. Link is good for ${pwResult.expires_hours || 24}h.`
                  : `Email couldn't be delivered right now — but the reset link below still works. Copy and send it to the user manually.`}
              </div>
              <p className="text-xs text-zinc-500">Reset URL (works even if the email didn't reach them):</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 font-mono text-[11px] bg-black/60 border border-white/10 px-3 py-3 text-white break-all select-all">
                  {pwResult.reset_url}
                </div>
                <Button onClick={() => copyPwValue(pwResult.reset_url)} className="btn-crimson rounded-none px-4 py-6" data-testid="pw-copy-link-button">
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                </Button>
              </div>
            </>
          )}

          <div className="flex justify-end pt-2">
            <Button onClick={() => setPwResult(null)} className="rounded-none bg-white/10 hover:bg-white/15 border border-white/15 text-white px-5 py-5" data-testid="pw-close-button">
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
